import React, { useEffect, useRef, useState } from "react";
import { Trash2, Plus, Shield, Upload, RefreshCcw, BarChart3, Film, Tv, Users, Clock, Star, X, Radio, Award, User2, Check, Ban, Search as SearchIcon, KeyRound, Mail, Crown, Clapperboard, Bell, ShoppingBag } from "lucide-react";
import { toast } from "sonner";
import { api } from "../../lib/api";

/* ---------------- Multiplex Screens admin ---------------- */
export default function ScreensAdmin() {
  const [config, setConfig] = useState(null);
  const [lineup, setLineup] = useState(null);
  const [count, setCount] = useState(10);
  const [saving, setSaving] = useState(false);
  const [regen, setRegen] = useState(false);
  const [pushBusy, setPushBusy] = useState(false);

  const reload = async () => {
    try {
      const [{ data: cfg }, { data: ln }] = await Promise.all([
        api.get("/admin/screens/config"),
        api.get("/screens"),
      ]);
      setConfig(cfg);
      setCount(cfg.screen_count);
      setLineup(ln);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load multiplex");
    }
  };
  useEffect(() => { reload(); }, []);

  const saveCount = async () => {
    if (count < 1 || count > (config?.max || 20)) {
      toast.error(`Must be between 1 and ${config?.max || 20}`);
      return;
    }
    setSaving(true);
    try {
      await api.post("/admin/screens/config", { screen_count: count });
      toast.success(`Multiplex now configured for ${count} screens`);
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const regenerate = async () => {
    setRegen(true);
    try {
      await api.post("/admin/screens/regenerate");
      toast.success("Today's lineup regenerated");
      await reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Regenerate failed");
    } finally {
      setRegen(false);
    }
  };

  const sendTonightPush = async () => {
    if (!confirm("Send the 'Tonight on the big screens' push to ALL active subscribers right now?")) return;
    setPushBusy(true);
    try {
      const { data } = await api.post("/admin/screens/push-tonight");
      toast.success(`Push delivered to ${data.delivered || 0} subscribers`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Push failed");
    } finally {
      setPushBusy(false);
    }
  };

  if (!config) return <p className="text-gray-500 text-sm">Loading multiplex config…</p>;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-xl font-bold mb-1 flex items-center gap-2"><Clapperboard className="w-5 h-5 text-[#E50914]" />GCR Theaters Multiplex</h2>
        <p className="text-sm text-gray-400">
          Multiple parallel screening rooms — paid subscribers pick a screen and the curtains rise on their movie. Daily lineup auto-rotates.
        </p>
      </div>

      {/* Config row */}
      <div className="bg-[#141414] border border-white/10 rounded-lg p-5">
        <h3 className="text-sm uppercase tracking-widest text-gray-400 mb-3">Configuration</h3>
        <div className="flex flex-wrap items-end gap-5">
          <div className="flex-1 min-w-[240px]">
            <label className="block text-xs text-gray-400 mb-2">Number of screens (1 – {config.max})</label>
            <div className="flex items-center gap-3">
              <input
                type="range"
                min={1}
                max={config.max}
                value={count}
                onChange={(e) => setCount(parseInt(e.target.value, 10))}
                className="flex-1 accent-[#E50914]"
                data-testid="screens-count-slider"
              />
              <input
                type="number"
                min={1}
                max={config.max}
                value={count}
                onChange={(e) => setCount(Math.max(1, Math.min(config.max, parseInt(e.target.value || "1", 10))))}
                className="w-20 bg-black/40 border border-white/10 rounded px-3 py-2 text-center font-bold text-lg tabular-nums focus:outline-none focus:border-[#E50914]"
                data-testid="screens-count-input"
              />
            </div>
            <p className="text-[11px] text-gray-500 mt-2">
              Default {config.default}. Changing this regenerates today's lineup automatically.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={saveCount}
              disabled={saving || count === config.screen_count}
              data-testid="screens-config-save"
              className="btn-red px-5 py-2.5 rounded font-semibold disabled:opacity-40 flex items-center gap-2"
            >
              {saving && <RefreshCcw className="w-4 h-4 animate-spin" />}
              Save & rebuild
            </button>
            <button
              onClick={regenerate}
              disabled={regen}
              data-testid="screens-regenerate"
              className="px-5 py-2.5 rounded border border-white/15 hover:border-white/40 font-semibold text-sm disabled:opacity-40 flex items-center gap-2"
            >
              {regen && <RefreshCcw className="w-4 h-4 animate-spin" />}
              Regenerate lineup
            </button>
            <button
              onClick={sendTonightPush}
              disabled={pushBusy}
              data-testid="screens-push-tonight"
              className="px-5 py-2.5 rounded border border-amber-500/40 hover:border-amber-400 text-amber-300 hover:text-amber-200 font-semibold text-sm disabled:opacity-40 flex items-center gap-2"
              title="Push notify all active subscribers about Screens 1-3 tonight"
            >
              {pushBusy ? <RefreshCcw className="w-4 h-4 animate-spin" /> : <Bell className="w-4 h-4" />}
              Push "Tonight on the big screens"
            </button>
          </div>
        </div>
      </div>

      {/* Today's lineup */}
      <div>
        <div className="flex items-center justify-between mb-3">
          <h3 className="text-sm uppercase tracking-widest text-gray-400">Today's lineup · {lineup?.date}</h3>
          <span className="text-xs text-gray-500" data-testid="screens-today-count">
            {lineup?.screens?.length || 0} screens active
          </span>
        </div>
        {!lineup?.screens?.length ? (
          <p className="text-gray-500 text-sm">No lineup yet — add some movies to the catalog first.</p>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3" data-testid="screens-admin-lineup">
            {lineup.screens.map((s) => (
              <div key={s.screen_no} className="bg-[#141414] border border-white/10 rounded-lg overflow-hidden">
                <div className="aspect-[16/10] relative bg-black/60">
                  {s.poster && <img src={s.poster} alt="" className="w-full h-full object-cover opacity-90" />}
                  <div className="absolute top-2 left-2 bg-black/70 backdrop-blur px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-amber-300 border border-amber-500/30">
                    {s.screen_label}
                  </div>
                  {s.is_pinned && (
                    <div className="absolute bottom-2 left-2 bg-blue-600/90 px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-white" title={`Pinned · ${s.pinned_kind} · until ${s.pinned_until}`}>
                      📌 Pinned · {s.pinned_kind}
                    </div>
                  )}
                  {s.is_premiere && (
                    <div className="absolute top-2 right-2 bg-[#E50914] px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-white">
                      ${s.ticket_price} Premiere
                    </div>
                  )}
                  {(s.geo_allow?.length || s.geo_block?.length) > 0 && (
                    <div className="absolute bottom-2 right-2 bg-black/80 backdrop-blur px-2 py-0.5 rounded text-[10px] uppercase tracking-widest font-bold text-cyan-300 border border-cyan-500/30" title={s.geo_allow?.length ? `Allow: ${s.geo_allow.join(",")}` : `Block: ${s.geo_block.join(",")}`}>
                      🌐 Geo-locked
                    </div>
                  )}
                </div>
                <div className="p-3">
                  <div className="font-bold text-sm line-clamp-2 leading-tight">{s.title}</div>
                  <div className="text-[10px] text-gray-500 mt-1 mb-2">{s.year} {s.duration_min ? `· ${s.duration_min}m` : ""}</div>
                  <AssignMovieControl screen={s} onChange={reload} />
                  <UploadAndPinControl screen={s} onChange={reload} />
                  <PremiereToggle screen={s} onChange={reload} />
                  <PromoteSocialButton screen={s} />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

/* ---------------- PremiereToggle: set/clear paid ticket on a screen ---------------- */
function PremiereToggle({ screen, onChange }) {
  const [editing, setEditing] = useState(false);
  const [price, setPrice] = useState(screen.ticket_price > 0 ? screen.ticket_price : 5);
  const [label, setLabel] = useState(screen.premiere_label || "Weekend Premiere");
  const [autoPromote, setAutoPromote] = useState(true);
  // Early-bird discount fields — optional, off by default
  const [discountEnabled, setDiscountEnabled] = useState(!!screen.discount_pct);
  const [discountPct, setDiscountPct] = useState(screen.discount_pct || 30);
  // Default deadline: 24h from now, formatted for datetime-local input (YYYY-MM-DDTHH:MM)
  const _defaultDeadline = () => {
    const d = new Date(Date.now() + 24 * 60 * 60 * 1000);
    return d.toISOString().slice(0, 16);
  };
  const [discountUntil, setDiscountUntil] = useState(
    screen.discount_until ? new Date(screen.discount_until).toISOString().slice(0, 16) : _defaultDeadline()
  );
  const [busy, setBusy] = useState(false);

  const save = async () => {
    if (price <= 0) { toast.error("Price must be > 0"); return; }
    if (discountEnabled) {
      if (discountPct < 1 || discountPct > 90) { toast.error("Discount % must be 1–90"); return; }
      if (!discountUntil || new Date(discountUntil).getTime() <= Date.now()) {
        toast.error("Discount deadline must be in the future"); return;
      }
    }
    setBusy(true);
    try {
      const body = { ticket_price: parseFloat(price), ticket_currency: "usd", label };
      if (discountEnabled) {
        body.discount_pct = parseInt(discountPct, 10);
        body.discount_label = "EARLY-BIRD";
        // Convert datetime-local (local TZ) → ISO UTC for the backend
        body.discount_until = new Date(discountUntil).toISOString();
      }
      await api.post(`/admin/screens/${screen.screen_no}/premiere`, body);
      const effective = discountEnabled
        ? (parseFloat(price) * (1 - discountPct / 100)).toFixed(2)
        : parseFloat(price).toFixed(2);
      toast.success(
        discountEnabled
          ? `${screen.screen_label}: $${price} → $${effective} (${discountPct}% EARLY-BIRD)`
          : `${screen.screen_label} is now a $${price} premiere`
      );
      if (autoPromote) {
        try {
          const { data } = await api.post(`/admin/screens/${screen.screen_no}/promote-social`, {});
          const igOk = data.instagram?.posted;
          const xOk = data.x?.posted;
          if (igOk || xOk) {
            toast.success(`Promoted to ${[igOk && "Instagram", xOk && "X"].filter(Boolean).join(" + ")}`);
          } else {
            toast.info("Marketing card saved — social posting not yet configured (set IG / X creds in .env)");
          }
        } catch (e) { console.warn("[promote-social]", e?.response?.status); }
      }
      setEditing(false);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  const clear = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/screens/${screen.screen_no}/premiere`);
      toast.success(`${screen.screen_label} is free again`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    } finally { setBusy(false); }
  };

  if (screen.is_premiere && !editing) {
    return (
      <div className="flex gap-1 mt-2" data-testid={`premiere-active-${screen.screen_no}`}>
        <button
          onClick={() => setEditing(true)}
          className="flex-1 text-[11px] px-2 py-1.5 rounded border border-amber-500/30 text-amber-300 hover:border-amber-400 hover:bg-amber-500/10 transition-colors"
        >
          Edit ${screen.ticket_price}
        </button>
        <button
          onClick={clear}
          disabled={busy}
          className="text-[11px] px-2 py-1.5 rounded border border-white/15 text-gray-400 hover:text-white hover:border-white/40 transition-colors"
          data-testid={`premiere-clear-${screen.screen_no}`}
        >
          Free
        </button>
      </div>
    );
  }

  if (editing) {
    return (
      <div className="space-y-1.5 mt-2 p-2 bg-black/30 rounded border border-white/10" data-testid={`premiere-edit-${screen.screen_no}`}>
        <div className="flex gap-1">
          <span className="text-xs text-gray-400 self-center">$</span>
          <input
            type="number"
            min={0.5}
            step="0.5"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            className="flex-1 bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
            data-testid={`premiere-price-${screen.screen_no}`}
          />
        </div>
        <input
          value={label}
          onChange={(e) => setLabel(e.target.value)}
          placeholder="Label (e.g. Weekend Premiere)"
          className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
        />
        {/* Early-bird countdown discount */}
        <label className="flex items-center gap-2 text-[11px] text-amber-200 cursor-pointer pt-1 border-t border-white/5 mt-1">
          <input
            type="checkbox"
            checked={discountEnabled}
            onChange={(e) => setDiscountEnabled(e.target.checked)}
            data-testid={`premiere-discount-toggle-${screen.screen_no}`}
            className="accent-amber-500"
          />
          🔥 Early-bird countdown discount
        </label>
        {discountEnabled && (
          <div className="space-y-1.5 bg-amber-500/5 border border-amber-500/20 rounded p-2 -mx-0.5">
            <div className="flex items-center gap-1">
              <input
                type="number"
                min={1} max={90}
                value={discountPct}
                onChange={(e) => setDiscountPct(e.target.value)}
                className="w-14 bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
                data-testid={`premiere-discount-pct-${screen.screen_no}`}
              />
              <span className="text-[10px] text-gray-400 uppercase tracking-widest">% off until</span>
            </div>
            <input
              type="datetime-local"
              value={discountUntil}
              onChange={(e) => setDiscountUntil(e.target.value)}
              className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] text-gray-200"
              data-testid={`premiere-discount-until-${screen.screen_no}`}
            />
            <div className="text-[10px] text-amber-300/80">
              Effective: ${(parseFloat(price || 0) * (1 - (parseFloat(discountPct) || 0) / 100)).toFixed(2)}
            </div>
          </div>
        )}
        <label className="flex items-center gap-2 text-[11px] text-gray-300 cursor-pointer">
          <input type="checkbox" checked={autoPromote} onChange={(e) => setAutoPromote(e.target.checked)}
            data-testid={`premiere-auto-promote-${screen.screen_no}`} className="accent-emerald-500" />
          📢 Auto-promote to Instagram + X
        </label>
        <div className="flex gap-1">
          <button onClick={save} disabled={busy} className="flex-1 btn-red py-1 rounded text-[11px]" data-testid={`premiere-save-${screen.screen_no}`}>
            {busy ? "…" : "Save"}
          </button>
          <button onClick={() => setEditing(false)} className="flex-1 py-1 rounded text-[11px] border border-white/10 text-gray-400">
            Cancel
          </button>
        </div>
      </div>
    );
  }

  return (
    <button
      onClick={() => setEditing(true)}
      data-testid={`premiere-enable-${screen.screen_no}`}
      className="w-full text-[11px] px-2 py-1.5 rounded border border-white/15 text-gray-400 hover:text-amber-300 hover:border-amber-500/40 transition-colors mt-2"
    >
      + Make a paid Premiere
    </button>
  );
}

/* ---------------- UploadAndPinControl: one-shot upload-new-movie + pin to screen ---------------- */
function UploadAndPinControl({ screen, onChange }) {
  const [open, setOpen] = useState(false);
  const [file, setFile] = useState(null);
  const [title, setTitle] = useState("");
  const [duration, setDuration] = useState("week");
  const [posterUrl, setPosterUrl] = useState("");
  const [trailerYt, setTrailerYt] = useState("");
  const [ticketPrice, setTicketPrice] = useState("");
  const [geoAllow, setGeoAllow] = useState("");
  const [geoBlock, setGeoBlock] = useState("");
  const [autoPromote, setAutoPromote] = useState(true);
  const [busy, setBusy] = useState(false);
  const [progress, setProgress] = useState(0);

  const reset = () => {
    setFile(null); setTitle(""); setPosterUrl(""); setTrailerYt("");
    setTicketPrice(""); setGeoAllow(""); setGeoBlock(""); setProgress(0);
  };

  const upload = async () => {
    if (!file) { toast.error("Pick a video file"); return; }
    if (!title.trim()) { toast.error("Enter the movie title"); return; }
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      fd.append("title", title.trim());
      fd.append("duration", duration);
      if (posterUrl) fd.append("poster_url", posterUrl);
      if (trailerYt) fd.append("trailer_yt", trailerYt);
      if (ticketPrice) fd.append("ticket_price", ticketPrice);
      if (geoAllow) fd.append("geo_allow", geoAllow);
      if (geoBlock) fd.append("geo_block", geoBlock);
      const { data } = await api.post(
        `/admin/screens/${screen.screen_no}/upload-and-assign`, fd,
        {
          headers: { "Content-Type": "multipart/form-data" },
          onUploadProgress: (e) => {
            if (e.total) setProgress(Math.round((e.loaded / e.total) * 100));
          },
        },
      );
      toast.success(`Uploaded & pinned "${data.video_title}" to ${screen.screen_label}${data.is_premiere ? ` ($${data.ticket_price} ticket)` : ""}`);
      if (autoPromote) {
        try {
          const { data: sd } = await api.post(`/admin/screens/${screen.screen_no}/promote-social`, {});
          const igOk = sd.instagram?.posted;
          const xOk = sd.x?.posted;
          if (igOk || xOk) {
            toast.success(`Promoted to ${[igOk && "Instagram", xOk && "X"].filter(Boolean).join(" + ")}`);
          } else {
            toast.info("Marketing card saved — social posting not yet configured");
          }
        } catch (e) { console.warn("[promote-social]", e?.response?.status); }
      }
      setOpen(false);
      reset();
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Upload failed");
    } finally {
      setBusy(false);
    }
  };

  if (!open) {
    return (
      <button
        onClick={() => setOpen(true)}
        data-testid={`upload-open-${screen.screen_no}`}
        className="w-full text-[11px] px-2 py-1.5 mb-1 rounded border border-emerald-500/30 text-emerald-300 hover:border-emerald-400 hover:bg-emerald-500/10 transition-colors"
      >
        📤 Upload new movie
      </button>
    );
  }

  return (
    <div className="space-y-1.5 mb-2 p-2 bg-black/30 rounded border border-emerald-500/20" data-testid={`upload-form-${screen.screen_no}`}>
      <input
        type="file"
        accept="video/*"
        onChange={(e) => setFile(e.target.files?.[0] || null)}
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] text-gray-300 file:mr-2 file:py-0.5 file:px-1.5 file:rounded file:border-0 file:bg-emerald-500/20 file:text-emerald-300 file:text-[10px]"
        data-testid={`upload-file-${screen.screen_no}`}
      />
      <input
        value={title}
        onChange={(e) => setTitle(e.target.value)}
        placeholder="Movie title (required)"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
        data-testid={`upload-title-${screen.screen_no}`}
      />
      <input
        value={posterUrl}
        onChange={(e) => setPosterUrl(e.target.value)}
        placeholder="Poster URL (optional)"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
      />
      <input
        value={trailerYt}
        onChange={(e) => setTrailerYt(e.target.value)}
        placeholder="YouTube trailer ID (optional)"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
      />
      <input
        value={ticketPrice}
        onChange={(e) => setTicketPrice(e.target.value)}
        placeholder="Pay-per-watch price USD (optional)"
        type="number"
        step="0.01"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
        data-testid={`upload-price-${screen.screen_no}`}
      />
      <div className="flex gap-1">
        {["day", "week", "month"].map(d => (
          <button
            key={d}
            type="button"
            onClick={() => setDuration(d)}
            className={`flex-1 text-[10px] py-1 rounded uppercase tracking-widest font-bold transition-colors ${duration === d ? "bg-emerald-600 text-white" : "border border-white/15 text-gray-400 hover:text-white"}`}
          >
            {d === "day" ? "Today" : d === "week" ? "1 Wk" : "4 Wks"}
          </button>
        ))}
      </div>
      <input
        value={geoAllow}
        onChange={(e) => setGeoAllow(e.target.value)}
        placeholder="Geo allow (US,GB,IN)"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
      />
      <input
        value={geoBlock}
        onChange={(e) => setGeoBlock(e.target.value)}
        placeholder="Geo block (CN,RU)"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
      />
      <label className="flex items-center gap-2 text-[11px] text-gray-300 cursor-pointer pt-1">
        <input type="checkbox" checked={autoPromote} onChange={(e) => setAutoPromote(e.target.checked)}
          data-testid={`upload-auto-promote-${screen.screen_no}`} className="accent-emerald-500" />
        📢 Auto-promote to Instagram + X
      </label>
      {busy && progress > 0 && (
        <div className="w-full bg-black/40 rounded h-1.5 overflow-hidden">
          <div className="h-full bg-emerald-500 transition-all" style={{ width: `${progress}%` }} />
        </div>
      )}
      <div className="flex gap-1">
        <button
          onClick={upload}
          disabled={busy || !file || !title.trim()}
          data-testid={`upload-save-${screen.screen_no}`}
          className="flex-1 bg-emerald-600 hover:bg-emerald-700 py-1 rounded text-[11px] font-bold disabled:opacity-50"
        >
          {busy ? (progress > 0 ? `${progress}%` : "Uploading…") : "Upload & Pin"}
        </button>
        <button
          onClick={() => { setOpen(false); reset(); }}
          className="flex-1 py-1 rounded text-[11px] border border-white/10 text-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}


/* ---------------- AssignMovieControl: pin a specific movie to a screen ---------------- */
function AssignMovieControl({ screen, onChange }) {
  const [open, setOpen] = useState(false);
  const [catalog, setCatalog] = useState([]);
  const [filter, setFilter] = useState("");
  const [videoId, setVideoId] = useState("");
  const [duration, setDuration] = useState("day");
  const [geoAllow, setGeoAllow] = useState("");
  const [geoBlock, setGeoBlock] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open || catalog.length) return;
    api.get("/videos", { params: { type: "movie", limit: 500 } })
      .then(r => setCatalog(r.data.filter(v => v.stream_url)))
      .catch(e => console.warn("[assign] catalog fetch failed", e?.response?.status));
  }, [open, catalog.length]);

  const filtered = catalog.filter(v =>
    !filter || v.title?.toLowerCase().includes(filter.toLowerCase())
  ).slice(0, 50);

  const save = async () => {
    if (!videoId) { toast.error("Pick a movie first"); return; }
    setBusy(true);
    try {
      const body = {
        video_id: videoId,
        duration,
        geo_allow: geoAllow.split(",").map(s => s.trim().toUpperCase()).filter(s => s.length === 2),
        geo_block: geoBlock.split(",").map(s => s.trim().toUpperCase()).filter(s => s.length === 2),
      };
      const { data } = await api.post(`/admin/screens/${screen.screen_no}/assign`, body);
      toast.success(`${screen.screen_label}: "${data.video_title}" locked until ${data.ends_at}`);
      setOpen(false);
      setVideoId(""); setFilter(""); setGeoAllow(""); setGeoBlock("");
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Assign failed");
    } finally { setBusy(false); }
  };

  const clearPin = async () => {
    setBusy(true);
    try {
      await api.delete(`/admin/screens/${screen.screen_no}/assign`);
      toast.success(`${screen.screen_label} returned to auto-rotation`);
      onChange?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Clear failed");
    } finally { setBusy(false); }
  };

  if (!open) {
    return (
      <div className="flex gap-1 mb-1">
        <button
          onClick={() => setOpen(true)}
          data-testid={`assign-open-${screen.screen_no}`}
          className="flex-1 text-[11px] px-2 py-1.5 rounded border border-blue-500/30 text-blue-300 hover:border-blue-400 hover:bg-blue-500/10 transition-colors"
        >
          {screen.is_pinned ? "Re-assign" : "Assign movie"}
        </button>
        {screen.is_pinned && (
          <button
            onClick={clearPin}
            disabled={busy}
            data-testid={`assign-clear-${screen.screen_no}`}
            className="text-[11px] px-2 py-1.5 rounded border border-white/15 text-gray-400 hover:text-white hover:border-white/40"
          >
            Auto
          </button>
        )}
      </div>
    );
  }

  return (
    <div className="space-y-1.5 mb-2 p-2 bg-black/30 rounded border border-blue-500/20" data-testid={`assign-form-${screen.screen_no}`}>
      <input
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
        placeholder="Search movies…"
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
        data-testid={`assign-filter-${screen.screen_no}`}
      />
      <select
        value={videoId}
        onChange={(e) => setVideoId(e.target.value)}
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-xs"
        data-testid={`assign-select-${screen.screen_no}`}
      >
        <option value="">— Pick movie —</option>
        {filtered.map(v => (
          <option key={v.id} value={v.id}>{v.title} {v.year ? `(${v.year})` : ""}</option>
        ))}
      </select>
      <div className="flex gap-1">
        {["day", "week", "month"].map(d => (
          <button
            key={d}
            type="button"
            onClick={() => setDuration(d)}
            data-testid={`assign-duration-${d}-${screen.screen_no}`}
            className={`flex-1 text-[10px] py-1 rounded uppercase tracking-widest font-bold transition-colors ${duration === d ? "bg-[#E50914] text-white" : "border border-white/15 text-gray-400 hover:text-white"}`}
          >
            {d === "day" ? "Today" : d === "week" ? "1 Wk" : "4 Wks"}
          </button>
        ))}
      </div>
      <input
        value={geoAllow}
        onChange={(e) => setGeoAllow(e.target.value)}
        placeholder="Geo allow (e.g. US,GB,IN)"
        title="Country codes (ISO-2) — only these regions can watch. Leave empty for no whitelist."
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
        data-testid={`assign-geo-allow-${screen.screen_no}`}
      />
      <input
        value={geoBlock}
        onChange={(e) => setGeoBlock(e.target.value)}
        placeholder="Geo block (e.g. CN,RU)"
        title="Country codes (ISO-2) — these regions are blocked. Ignored when 'allow' is set."
        className="w-full bg-black/40 border border-white/15 rounded px-2 py-1 text-[10px] font-mono"
        data-testid={`assign-geo-block-${screen.screen_no}`}
      />
      <div className="flex gap-1">
        <button
          onClick={save}
          disabled={busy || !videoId}
          data-testid={`assign-save-${screen.screen_no}`}
          className="flex-1 btn-red py-1 rounded text-[11px] disabled:opacity-50"
        >
          {busy ? "…" : "Lock in"}
        </button>
        <button
          onClick={() => setOpen(false)}
          className="flex-1 py-1 rounded text-[11px] border border-white/10 text-gray-400"
        >
          Cancel
        </button>
      </div>
    </div>
  );
}



/* ---------------- PromoteSocialButton: manual re-post any screen to social ---------------- */
function PromoteSocialButton({ screen }) {
  const [busy, setBusy] = useState(false);

  const promote = async () => {
    setBusy(true);
    try {
      const { data } = await api.post(`/admin/screens/${screen.screen_no}/promote-social`, {});
      const igOk = data.instagram?.posted;
      const xOk = data.x?.posted;
      if (igOk || xOk) {
        toast.success(`📢 Promoted to ${[igOk && "Instagram", xOk && "X"].filter(Boolean).join(" + ")}`);
      } else {
        toast.info(
          <div>
            <div>Marketing card generated but no platforms configured.</div>
            {data.card_url && (
              <a href={data.card_url} target="_blank" rel="noreferrer" className="text-emerald-400 underline text-xs">
                Download card →
              </a>
            )}
          </div>,
          { duration: 6000 },
        );
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Promote failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <button
      onClick={promote}
      disabled={busy}
      data-testid={`promote-social-${screen.screen_no}`}
      className="w-full text-[11px] px-2 py-1.5 mt-1 rounded border border-purple-500/30 text-purple-300 hover:border-purple-400 hover:bg-purple-500/10 transition-colors disabled:opacity-50"
    >
      {busy ? "📢 Posting…" : "📢 Promote on social now"}
    </button>
  );
}
